import { useState } from 'react';

import { GroupBase, OnChangeValue } from 'react-select-5';

import { FeatureRichSelectProps } from '../../index.types';
import { Controls, Props } from './types';

export function useOptionToString<
  Option = unknown,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  controls: Controls<Option, IsMulti, Group>,
  props: Props<FeatureRichSelectProps<Option, IsMulti, Group>, Option, IsMulti, Group>,
) {
  const { value, selectRef } = controls;

  const { isMulti, async, options: pOptions, getOptionValue: pGetOptionValue } = props;

  // Keep this options array inside the hook because it is not passed
  // to the select and is only used here to find an option by its value
  const [options, setOptions] = useState<NonNullable<FeatureRichSelectProps<Option, IsMulti, Group>['options']>>([]);

  const getOptionValue =
    pGetOptionValue ||
    selectRef.current?.getOptionValue ||
    ((option: Option) => {
      if (typeof option === 'object' && option !== null && 'value' in option) return (option as { value: any }).value;
      return undefined;
    });

  const modifiedLoadOptions =
    async &&
    props.loadOptions &&
    (async (...p: Parameters<NonNullable<(typeof props)['loadOptions']>>) => {
      if (!props.async || !props.loadOptions) return [];
      const loadedOptions = await props.loadOptions(...p);
      if (!loadedOptions) return [];
      setOptions(loadedOptions);
      return loadedOptions;
    });

  const findOption = (
    optionsOrGroups: typeof options,
    value?: (typeof controls)['value']['value'],
  ): Option | undefined => {
    if (!optionsOrGroups || !getOptionValue) return undefined;

    const option = optionsOrGroups.reduce(
      (found, optionOrGroup) => {
        if (found) return found;
        if (typeof optionOrGroup === 'object' && optionOrGroup !== null && 'options' in optionOrGroup) {
          return findOption(optionOrGroup.options, value);
        }
        return getOptionValue(optionOrGroup) === value ? optionOrGroup : undefined;
      },
      undefined as Option | undefined,
    );

    return option;
  };

  const handleChange = (optionOrOptions: OnChangeValue<Option, IsMulti>) => {
    if (!value.setValue || !getOptionValue) return;

    // TOFIX: Figure out why optionOrOptions type does not depend on isMulti
    if (isMulti) {
      const options = (optionOrOptions as OnChangeValue<Option, true>).map((option) => getOptionValue(option));
      value.setValue<true>(options);
      return;
    }

    value.setValue<false>(
      optionOrOptions && getOptionValue(optionOrOptions as NonNullable<OnChangeValue<Option, false>>),
    );
  };

  if (!props.enabled || !getOptionValue)
    return {
      enabled: false,
      controls: undefined,
    };

  return {
    enabled: true,
    controls: {
      loadOptions: modifiedLoadOptions || undefined,
      value: findOption(pOptions || options, value.value),
      onChange: handleChange,
    },
  };
}
